import React, { useState } from 'react';
import { Accordion, Button } from '@saleshandy/design-system';
import { ChevronDown, ChevronUp } from '@saleshandy/icons';

import Checkbox from '../../../../../../../shared/design-system/components/atoms/checkbox';
import Transfer from '../../../../../../../shared/design-system/components/transfer';
import { Placement } from '../../../../../../../shared/design-system/components/overlay';

import { MultiLevelSelectItem, MultiLevelSelectOption } from '../utils/types';

import { Images } from '../../../../../../../shared/app-constants';
import { accessibleOnClick } from '../../../../../../../shared/utils/accessible-on-click';
import {
  getFilterOptionsForMultiLevelSelectDropdown,
  getSelectedItemsForMultiLevelSelectDropdown,
  getSelectedKeysForMultiLevelSelectDropdown,
  onSelectDeselectMultiLevelDropdownItem,
  onSelectDeselectMultiLevelDropdownOption,
} from '../utils/helper';
import { renderPills } from '../utils/render-pills';

type IProps = {
  options: MultiLevelSelectOption[];
  value: string[];
  onChange: (keys: string[]) => void;
  isLoading?: boolean;
  placeholder?: string;
  disabled: boolean;
  className?: string;
};

const ProspectFilterMultiLevelSelect: React.FC<IProps> = ({
  options,
  value: selectedKeys,
  onChange: setSelectedKeys,
  isLoading = false,
  placeholder = 'Select',
  disabled,
  className = '',
}) => {
  const [show, setShow] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);

  const onSelectDeselect = (option: MultiLevelSelectOption) => {
    const updatedSelectedKeys = onSelectDeselectMultiLevelDropdownOption(
      option,
      selectedKeys,
    );
    setSelectedKeys(updatedSelectedKeys);
  };

  const onItemSelectDeselect = (item: MultiLevelSelectItem) => {
    const updatedSelectedKeys = onSelectDeselectMultiLevelDropdownItem(
      item,
      selectedKeys,
    );
    setSelectedKeys(updatedSelectedKeys);
  };

  return (
    <Transfer<MultiLevelSelectOption>
      show={show}
      onToggle={(v) => {
        if (!disabled) {
          setShow(v);
        }
      }}
      showSearch
      className="team-filter-select prospect-filters-owners-dropdown subsequences-filters-dropdown"
      multiSelect
      placement={Placement.BottomStart}
      options={options}
      selectedOptionKey={getSelectedKeysForMultiLevelSelectDropdown(
        options || [],
        selectedKeys,
      )}
      optionFilterProp="value"
      searchPlaceholder="Search"
      customFilterOption={(_, searchValue) => {
        const filteredOptions = getFilterOptionsForMultiLevelSelectDropdown(
          options,
          searchValue,
        );

        if (filteredOptions?.length === 0 && !isEmpty) {
          setIsEmpty(true);
        } else if (filteredOptions?.length !== 0 && isEmpty) {
          setIsEmpty(false);
        }

        return filteredOptions;
      }}
      onSelect={onSelectDeselect}
      onDeselect={onSelectDeselect}
      optionRenderer={(option: MultiLevelSelectOption) => (
        <Accordion
          allowMultipleExpanded
          allowZeroExpanded
          preExpanded={[option.key]}
        >
          <Accordion.Item uuid={option.key}>
            <Accordion.ItemHeading>
              <Accordion.ItemButton>
                <span>{option.value}</span>
                <Accordion.ItemState>
                  {({ expanded }) =>
                    expanded ? <ChevronUp /> : <ChevronDown />
                  }
                </Accordion.ItemState>
              </Accordion.ItemButton>
            </Accordion.ItemHeading>
            {option?.items?.length > 0 && (
              <Accordion.ItemPanel>
                {option?.items?.map((item) => (
                  <div
                    className="team-member"
                    {...accessibleOnClick(() => {
                      onItemSelectDeselect(item);
                    })}
                  >
                    <Checkbox checked={selectedKeys?.includes(item?.key)} />
                    <span className="team-member-name">{item?.value}</span>
                  </div>
                ))}
              </Accordion.ItemPanel>
            )}
          </Accordion.Item>
        </Accordion>
      )}
      isLoading={isLoading}
      showOptionsSeparator={true}
      clickListenerOn="checkbox"
      customEmptyElement={
        <>
          <img src={Images.NoResult} alt="No Result" />
          <h1>No Result Found</h1>
          <p>Please try searching with another term</p>
        </>
      }
    >
      <Button
        className={`transfer-select-btn btn-white w-1 prospect-filters-owners-select ${className}`}
        variant="tertiary"
        onClick={() => {
          if (!disabled) {
            setShow(!show);
          }
        }}
      >
        <div className="transfer-select-btn-content">
          {!selectedKeys || selectedKeys?.length === 0 ? (
            <span className="placeholder">{placeholder}</span>
          ) : (
            renderPills(
              getSelectedItemsForMultiLevelSelectDropdown(
                options,
                selectedKeys,
              ),
              setSelectedKeys,
              disabled,
            )
          )}
        </div>
        <div className="bs-select-box-icon-wrapper">
          {show ? <ChevronUp /> : <ChevronDown />}
        </div>
      </Button>
    </Transfer>
  );
};

export default ProspectFilterMultiLevelSelect;
